import React from 'react';
import SectionItem from './SectionItem';
import ContentBlockSection from './ContentBlockSection';
import SpacerSection from './SpacerSection';
import { CharityHeroStrip, CharitySidebar } from './CharityHeroSection';
import { getSectionExclusiveRole, getSectionMeta } from '../config/sectionRegistry';
import { useTranslations } from '../contexts/TranslationContext';

/**
 * DynamicSections — renders section types from state that are NOT in the layout's
 * hardcoded allSections array. This enables newly-added sections (via SectionManager)
 * to appear on page without requiring each layout to hardcode every possible type.
 *
 * For known section types, maps to existing components.
 * For unknown types, shows an admin-only placeholder.
 */
export default function DynamicSections({ sections = [], knownTypes = [], editMode = false, gameType }) {
  const { t } = useTranslations();
  if (!sections || sections.length === 0) return null;

  const isRenderable = (section) => {
    if (!section?.section_type) return false;
    if (section.is_visible === false) return false;
    if (section.config?.__removed) return false;
    return true;
  };

  const knownVisibleRoles = new Set(
    sections
      .filter(section => isRenderable(section) && knownTypes.includes(section.section_type))
      .map(section => getSectionExclusiveRole(section.section_type))
      .filter(Boolean)
  );

  const hasDynamicRenderer = (sectionType) => (
    sectionType.startsWith('content_block_') ||
    sectionType.startsWith('spacer_') ||
    [
      'charity_hero',
      'charity_strip',
      'charity_info_card',
      'charity_impact',
    ].includes(sectionType)
  );

  // Find section types that are visible, not in the layout's hardcoded list, and not tombstoned
  const dynamicSections = sections
    .filter(s => {
      if (!isRenderable(s)) return false;
      if (knownTypes.includes(s.section_type)) return false;
      const role = getSectionExclusiveRole(s.section_type);
      if (role && knownVisibleRoles.has(role)) return false;
      return true;
    })
    .sort((a, b) => (a.section_order || 0) - (b.section_order || 0))
    .filter((section, index, sortedSections) => {
      const role = getSectionExclusiveRole(section.section_type);
      if (!role) return true;
      if (!hasDynamicRenderer(section.section_type)) return true;
      return sortedSections.findIndex(item => (
        getSectionExclusiveRole(item.section_type) === role && hasDynamicRenderer(item.section_type)
      )) === index;
    });

  if (dynamicSections.length === 0) return null;

  return (
    <>
      {dynamicSections.map(section => {
        const rendered = renderDynamicSection(section, { editMode, gameType, sections, t });
        if (!rendered) return null;
        const meta = getSectionMeta(section.section_type);
        return (
          <SectionItem
            key={section.id || section.section_type}
            sectionType={section.section_type}
            editMode={editMode}
            label={meta?.label || section.section_type}
          >
            {rendered}
          </SectionItem>
        );
      })}
    </>
  );
}

function renderDynamicSection(section, { editMode, gameType, sections, t }) {
  const { section_type, config = {} } = section;

  // Content blocks
  if (section_type.startsWith('content_block_')) {
    return <ContentBlockSection sectionType={section_type} />;
  }

  // Spacers
  if (section_type.startsWith('spacer_')) {
    return <SpacerSection sectionType={section_type} sections={sections} />;
  }

  // Charity sections
  switch (section_type) {
    case 'charity_hero':
      return (
        <CharitySidebar
          config={{ ...config, placement: 'sidebar', showSidebar: true }}
          gameType={gameType}
          gameData={{}}
        />
      );
    case 'charity_strip':
      return (
        <CharityHeroStrip
          config={{ ...config, placement: 'hero_strip' }}
          gameType={gameType}
          gameData={{}}
        />
      );
    case 'charity_info_card':
      return (
        <CharitySidebar
          config={{ ...config, placement: 'sidebar', showSidebar: true }}
          gameType={gameType}
          gameData={{}}
        />
      );
    case 'charity_impact':
      return (
        <CharitySidebar
          config={{ ...config, placement: 'sidebar', showSidebar: true }}
          gameType={gameType}
          gameData={{}}
        />
      );
    default:
      break;
  }

  // For section types without a dedicated component, show admin-only placeholder
  if (editMode) {
    const meta = getSectionMeta(section_type);
    return (
      <div className="w-full border-2 border-dashed border-blue-300/60 dark:border-blue-700/60 rounded-lg p-6 flex flex-col items-center justify-center gap-2 bg-blue-50/50 dark:bg-blue-900/20">
        <span className="text-2xl">{meta?.icon || '📦'}</span>
        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
          {section_type ? (() => { const k = `admin.sections.labels.${section_type}`; const v = t(k); return (v && v !== k) ? v : (meta?.label || section_type); })() : (meta?.label || section_type)}
        </span>
        <span className="text-xs text-blue-400 dark:text-blue-500">
          {t('admin.sections.componentComingSoon')}
        </span>
      </div>
    );
  }

  // Non-admin: don't render unknown section types
  return null;
}
